import { UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useClients } from 'src/clients/hooks/useClients';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useOwnerNames } from 'src/financial/financial-data/hooks/useOwnerNames';
import { recordedValue } from 'src/financial/financial-data/ownerShares';
import { valueFieldFor } from 'src/financial/financial-data/financial-item/rework/financialItemRework';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import {
  BLANK_OWNER_NAME_ERROR,
  OwnersEditor,
  blankExternalNameIndexes,
  isExternalOwner,
  shareSaveError,
  type OwnerRowValue,
} from './OwnersEditor';

export type { OwnerRowValue } from './OwnersEditor';
export { ownersSumTo100, productSupportsOwners } from './OwnersEditor';

export const OWNER = 'Owner';

// Each owner row is XOR-clean (client_id OR external_name) so the backend's
// exactly-one validator passes; the counter/UI keys are dropped.
export function normaliseOwnersForPayload(owners: OwnerRowValue[]): OwnerRowValue[] {
  return owners.map(owner => {
    const percentage =
      owner.ownership_percentage == null ? null : Number(owner.ownership_percentage);
    const identity = owner.client_id
      ? { client_id: owner.client_id }
      : { external_name: owner.external_name ?? '' };
    return { ...(owner.id ? { id: owner.id } : {}), ...identity, ownership_percentage: percentage };
  });
}

type OwnersPayloadFragment = { owners?: OwnerRowValue[] };

/** What the create flow's owner step hands the create form, in the navigation state. */
export type CreateFlowState = {
  owners: OwnerRowValue[];
  /**
   * Whether the client in the address must stay an owner. A client's own page keeps them, since
   * the item is opened through them afterwards. The family view only anchors the address on the
   * first member picked, so any member can come off and the item is created through one who stays.
   */
  holderLocked: boolean;
};

// Resolves the `owners` fragment to merge into a financial-item save payload, shared
// by every product form. Returns null when the owners are not yet saveable — the caller
// must abort the save; the reason is put against the field at fault where there is one,
// and the form is passed in so it can be.
//
// The recorded rows are always sent, because the backend replaces the stored owner rows with the
// list it is given. Leaving out a lone owner left behind any co-owner the adviser had just removed
// in making the item sole, and the item still showed on that co-owner's page. With the flag off,
// or with no rows recorded, the `owners` key is omitted entirely rather than sent as []:
// EntityManager only reconciles when owners is present, so an empty list would delete the
// backfilled owner row. Omitting it round-trips the item unchanged.
export function resolveOwnersPayload(
  values: FormValuesType,
  flagEnabled: boolean,
  form?: UseFormReturnType<FormValuesType>,
): OwnersPayloadFragment | null {
  const owners = flagEnabled ? ((values[OWNER] as OwnerRowValue[] | undefined) ?? []) : [];

  // The backend blanks '' to None and rejects a nameless owner, which came back as the generic
  // save error. Asking against the row says which name is missing, and nothing is sent.
  const blankNames = blankExternalNameIndexes(owners);
  if (blankNames.length > 0) {
    blankNames.forEach(index =>
      form?.setFieldError(`${OWNER}.${index}.external_name`, BLANK_OWNER_NAME_ERROR),
    );
    return null;
  }

  const shareError = shareSaveError(owners);
  if (shareError) {
    notifications.show({
      title: 'Check the ownership shares',
      message: shareError,
      color: 'red',
    });
    return null;
  }

  return owners.length > 0 ? { owners: normaliseOwnersForPayload(owners) } : {};
}

type Props = {
  product: string;
  variant?: 'classic' | 'valuation';
  /** Lets the Valuation section explain the external slice only when there is one. */
  onExternalOwnersChange?: (hasExternalOwners: boolean) => void;
};

// Create-form adapter: bridges the dynamic form + the owners the create flow handed over to the
// controlled OwnersEditor, and keeps the form's `Owner` list and `is_joint` fields in sync.
export function OwnerSharesSection({ product, variant, onExternalOwnersChange }: Props) {
  const currency = useCurrency();
  const { form, isEditing, isCreateMode } = useDynamicUI();
  const location = useLocation();
  const { members } = useFamilyMembers();
  // The route param reads "undefined" when a link was built without a client.
  const { clientId: routeClientId } = useCurrentClientIfAvailable();
  const clientId = routeClientId && routeClientId !== 'undefined' ? routeClientId : undefined;
  const resolveOwnerName = useOwnerNames(members, clientId ? [clientId] : []);
  const { flagEnabledFinancialItemOwners } = useFlags();
  const { clients } = useClients({}, { enabled: !!flagEnabledFinancialItemOwners });

  const productValues = (form.getValues()[product] ?? {}) as Record<string, unknown>;
  const jointFromCreateFlow = new URLSearchParams(location.search).get('joint') === 'true';
  const createFlowState = location.state as Partial<CreateFlowState> | null;
  const holderLocked = isCreateMode && createFlowState?.holderLocked !== false;

  // Existing owners (edit, or already seeded) win; otherwise take the ones the create flow's
  // owner step chose. They travel in the navigation state rather than the address, which an
  // external owner's name has no business being in. Edit routes carry no state, so no
  // isCreateMode guard is needed.
  const [owners, setOwners] = useState<OwnerRowValue[]>(() => {
    const existing = (form.getValues()[OWNER] as OwnerRowValue[] | undefined) ?? [];
    if (existing.length > 0) return existing;
    return (createFlowState?.owners ?? []).map(owner => ({ ...owner, ownership_percentage: null }));
  });

  // The switch has to agree with what the rows say, because the backend reads an item naming
  // two owners as joint however the switch was left. Shown off over a recorded split, it hid
  // the rows behind a switch that turned itself back on at the next save. It is also forced on
  // by the create flow's owner step, which picks the co-owners before this form is reached and
  // sets the form field only in a later effect.
  const [isJoint, setIsJoint] = useState(
    () => productValues.is_joint === 'true' || jointFromCreateFlow || owners.length > 1,
  );

  // Only an edit in progress writes back. `useForm` hands out a fresh object each render, so an
  // unguarded effect re-runs on every one — including the render after Cancel, where it would put
  // the abandoned owners back over the values `form.reset()` had just restored.
  useEffect(() => {
    if (!isEditing) return;
    form.setFieldValue(OWNER, owners);
  }, [owners, form, isEditing]);

  // Several owners make the item joint whatever the switch was last set to: the editor locks it on
  // while they are listed, and the form saves what it shows.
  const savesJoint = isJoint || owners.length > 1;
  useEffect(() => {
    if (!isEditing) return;
    form.setFieldValue(`${product}.is_joint`, savesJoint ? 'true' : 'false');
  }, [savesJoint, product, form, isEditing]);

  // The form is uncontrolled, so reading the value during render leaves each owner's slice on
  // whatever it was when the section last rendered, and typing a new valuation above leaves the
  // money column showing slices of the old one. Watching the field re-renders the rows as it
  // changes; the rendered value is still the floor, so a value loaded after mount is not missed.
  // Undefined until the field is first watched; null once it is watched empty.
  const [watchedTotal, setWatchedTotal] = useState<number | null | undefined>(undefined);
  const valueField = valueFieldFor(product);
  form.watch(`${product}.${valueField}`, ({ value }) => setWatchedTotal(recordedValue(value)));
  const total =
    watchedTotal === undefined ? recordedValue(productValues[valueField]) : watchedTotal;

  // Cancelling an edit calls `form.reset()`, which restores the form's own fields but cannot
  // reach this local state, so the abandoned values would be shown and saved by the next
  // unrelated edit. Outside an edit the form holds the truth; mirror it back. `reset` notifies
  // no watch subscriber either, so the watched total is dropped rather than mirrored — the
  // restored field is read at render until the next edit moves it. Create mode never leaves
  // editing, so the owners seeded from the create flow are not touched here.
  useEffect(() => {
    if (isEditing) return;
    const values = form.getValues();
    const saved = (values[OWNER] as OwnerRowValue[] | undefined) ?? [];
    setOwners(saved);
    setIsJoint(
      (values[product] as Record<string, unknown> | undefined)?.is_joint === 'true' ||
        saved.length > 1,
    );
    setWatchedTotal(undefined);
  }, [isEditing, form, product]);

  // A blocked save marks the rows with no name. Typing one clears its error rather than leaving
  // it under a field that now reads fine, which setFieldValue on the whole list does not do.
  const handleOwnersChange = (next: OwnerRowValue[]) => {
    const stillBlank = new Set(blankExternalNameIndexes(next));
    for (let index = 0; index < Math.max(owners.length, next.length); index += 1) {
      if (!stillBlank.has(index)) form.clearFieldError(`${OWNER}.${index}.external_name`);
    }
    setOwners(next);
  };

  // The switch moves the rows with it, because the rows are what gets saved. A joint item needs
  // at least one named owner for "Co-owner not recorded" to show against, and the holder is the
  // one owner always known, so their row goes in.
  //
  // The editor only offers the switch off once a single owner is left, and a solely held item
  // has no split for that owner's share to describe, so the share goes.
  const handleJointChange = (joint: boolean) => {
    setIsJoint(joint);
    if (joint) {
      if (owners.length === 0 && clientId) {
        setOwners([{ client_id: clientId, ownership_percentage: null }]);
      }
      return;
    }
    setOwners(owners.map(owner => ({ ...owner, ownership_percentage: null })));
  };

  // Lets the Valuation section explain the external slice only when there is one.
  useEffect(() => {
    onExternalOwnersChange?.(owners.some(isExternalOwner));
  }, [owners, onExternalOwnersChange]);

  return (
    <OwnersEditor
      owners={owners}
      onChange={handleOwnersChange}
      members={members}
      resolveOwnerName={resolveOwnerName}
      otherClients={clients}
      total={total}
      formatAmount={amount => formatMoney(amount, currency)}
      showJointToggle
      isJoint={isJoint}
      onJointChange={handleJointChange}
      readOnly={!isEditing}
      // A new item is opened through the client it is created from, so that client stays an
      // owner. An edit can remove them: the item keeps its holder and stays with its owners.
      lockedClientId={holderLocked ? clientId : undefined}
      nameErrors={ownerNameErrors(form, owners.length)}
      variant={variant}
    />
  );
}

// Blank-name errors a blocked save put on the form, read back by row so the editor can show each
// against its own field.
function ownerNameErrors(
  form: UseFormReturnType<FormValuesType>,
  ownerCount: number,
): Record<number, string> {
  const errors: Record<number, string> = {};
  for (let index = 0; index < ownerCount; index += 1) {
    const error = form.errors[`${OWNER}.${index}.external_name`];
    if (typeof error === 'string') errors[index] = error;
  }
  return errors;
}
